import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, statusTypesData } from "@/data";
import { createBulkDistributionsMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayBulkDistributionResultsHandler, displayErrorHandler, normalizeDateToUTCMidnightHandler } from "@/utils";

const useCreateBulkDistributions = (ids, setBulkResults) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [createBulkDistributionsAction, { loading }] = useMutation(createBulkDistributionsMutation);

    const createBulkDistributions = async ({
        availableForImmersiveDate,
        client,
        fileType,
        isApple,
        preOrderDate,
        preOrderType,
        releaseDate,
        salesStartDate,
        streamStartDate,
        territory,
        track,
        trackFormat,
    }) => {
        try {
            if (ids.length <= import.meta.env.VITE_RELEASES_DOWNLOAD_LIMIT) {
                await createBulkDistributionsAction({
                    onCompleted: ({ bulkDistribute }) =>
                        displayBulkDistributionResultsHandler(
                            bulkDistribute?.results,
                            t("messages.distributionMsgs.bulk.success"),
                            setBulkResults,
                            dispatch,
                            t,
                        ),
                    variables: {
                        clientId: client,
                        distributionFileTypeId: fileType,
                        ...(releaseDate && !isApple && { releaseDate }),
                        releasesIds: ids,
                        territoryId: territory,
                        tracksFormat: trackFormat.map(({ value }) => value),
                        ...(isApple && {
                            extendedDetails: {
                                availableForImmersiveDate: normalizeDateToUTCMidnightHandler(availableForImmersiveDate),
                                salesStartDate: normalizeDateToUTCMidnightHandler(salesStartDate),
                                streamStartDate: normalizeDateToUTCMidnightHandler(streamStartDate),
                                ...(preOrderType &&
                                    preOrderDate && {
                                        tracksPreOrder: {
                                            preOrderType,
                                            preorderSalesStartDate: normalizeDateToUTCMidnightHandler(preOrderDate),
                                            ...(track &&
                                                track?.length > 0 && { trackIds: track.map(({ value }) => value) }),
                                        },
                                    }),
                            },
                        }),
                    },
                });
            } else {
                dispatch(
                    openAlert(
                        t("messages.distributionMsgs.entitiesLengthError", { entity: t("releases.name") }),
                        statusTypesData.error,
                    ),
                );
            }
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    return {
        createBulkDistributions,
        loading,
    };
};

export default useCreateBulkDistributions;
